import ColorModeContext from '@/contexts/colorModeContext';
import {useBrand} from '@/contexts/BrandContext';
import {useApolloClient} from '@apollo/client';
import {
  AccountCircle,
  Campaign,
  Logout,
  MailRounded,
  MenuBook,
} from '@mui/icons-material';
import {Box, Divider, FormControlLabel, MenuItem} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {userContext, Modal} from '@ark/web';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {cachePersistor} from '../../main';
import {ModeSwitch} from '@/layout/ModeSwitch';

type ProfileMenuContentProps = {
  onSelect: () => void;
};

export const ProfileMenuContent = (props: ProfileMenuContentProps) => {
  const navigate = useNavigate();
  const {name: brandName, supportEmail, supportContactName} = useBrand();
  const {pathname} = useLocation();
  const client = useApolloClient();
  const {signOut} = React.useContext(userContext);
  const theme = useTheme();
  const [openConfirmLogoutModal, setOpenConfirmLogoutModal] = useState(false);
  const {enqueueSnackbar} = useSnackbar();
  return (
    <Box
      sx={{
        minWidth: 180,
        py: 0.5,
      }}
    >
      <MenuItem
        selected={pathname.startsWith('/profile')}
        onClick={() => {
          props.onSelect();
          navigate('/profile');
        }}
        sx={{
          py: 1,
          px: 1.5,
          gap: 1.5,
          fontSize: 14,
          borderRadius: '4px',
          mx: 0.5,
          '&.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
            '&:hover': {
              backgroundColor: theme.palette.action.selected,
            },
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <AccountCircle
          sx={{fontSize: 18, color: theme.palette.text.secondary}}
        />
        Profile
      </MenuItem>
      <MenuItem
        onClick={() => {
          props.onSelect();
          window.open(
            `mailto:${supportEmail}?subject=I'd like some help&body=Hi ${supportContactName},...`,
          );
        }}
        sx={{
          py: 1,
          px: 1.5,
          gap: 1.5,
          fontSize: 14,
          borderRadius: '4px',
          mx: 0.5,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <MailRounded sx={{fontSize: 18, color: theme.palette.text.secondary}} />
        Support
      </MenuItem>
      <MenuItem
        onClick={() => {
          props.onSelect();
          window.open(
            'https://ark-rent.notion.site/ark-rent/Building-Better-Communities-User-Guide-4de3b241d35d45a2ba534d58ec49bd15',
          );
        }}
        sx={{
          py: 1,
          px: 1.5,
          gap: 1.5,
          fontSize: 14,
          borderRadius: '4px',
          mx: 0.5,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <MenuBook sx={{fontSize: 18, color: theme.palette.text.secondary}} />
        User Guide
      </MenuItem>
      <MenuItem
        selected={pathname.startsWith('/features')}
        onClick={() => {
          props.onSelect();
          navigate('/features');
        }}
        sx={{
          py: 1,
          px: 1.5,
          gap: 1.5,
          fontSize: 14,
          borderRadius: '4px',
          mx: 0.5,
          '&.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
            '&:hover': {
              backgroundColor: theme.palette.action.selected,
            },
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Campaign sx={{fontSize: 18, color: theme.palette.text.secondary}} />
        Features
      </MenuItem>
      <Divider sx={{my: 0.5}} />
      <MenuItem
        onClick={async () => {
          setOpenConfirmLogoutModal(true);
        }}
        sx={{
          py: 1,
          px: 1.5,
          gap: 1.5,
          fontSize: 14,
          borderRadius: '4px',
          mx: 0.5,
          color: theme.palette.error.main,
          '&:hover': {
            backgroundColor: theme.palette.error.light + '20',
          },
        }}
      >
        <Logout sx={{fontSize: 18}} />
        Log Out
      </MenuItem>
      <Divider sx={{my: 0.5}} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 0.5,
        }}
      >
        <ColorModeContext.Consumer>
          {({toggleColorMode}) => (
            <FormControlLabel
              onChange={toggleColorMode}
              control={
                <ModeSwitch
                  sx={{m: 0.5}}
                  checked={theme.palette.mode === 'dark'}
                />
              }
              label=""
              labelPlacement="start"
            />
          )}
        </ColorModeContext.Consumer>
      </Box>
      <Modal
        modalOpen={openConfirmLogoutModal}
        setModalOpen={setOpenConfirmLogoutModal}
        title="Logout"
        description={`Are you sure you want to logout of ${brandName}?`}
        onConfirm={async () => {
          let cleanupFailed = false;
          try {
            await cachePersistor.purge();
          } catch (error) {
            cleanupFailed = true;
          }
          try {
            await client.clearStore();
          } catch (error) {
            cleanupFailed = true;
          }
          if (cleanupFailed) {
            enqueueSnackbar('Error logging out, please try again', {
              variant: 'error',
            });
          }
          signOut();
          setOpenConfirmLogoutModal(false);
        }}
      />
    </Box>
  );
};
